import { useEffect, useMemo, useRef, useState } from 'react'
import BlurHashImage from '@components/BlurHashImage'
import { BLUR_HASH_MAP } from '@constants/BlurHashItems'
import { imageLoadCache } from '@utils/image-load-cache'
import './UtilityFloorPlan.css'

const clamp = (value, min, max) => Math.max(min, Math.min(max, value))

const toPercentNumber = (value) => {
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace('%', ''))
    return Number.isFinite(parsed) ? clamp(parsed, 0, 100) : 0
  }
  if (!Number.isFinite(value)) return 0
  if (value <= 1 && value >= 0) {
    return clamp(value * 100, 0, 100)
  }
  return clamp(value, 0, 100)
}

const toRadians = (deg) => (deg * Math.PI) / 180

const getSectorPath = (cx, cy, radius, startDeg, endDeg) => {
  const startRad = toRadians(startDeg - 90)
  const endRad = toRadians(endDeg - 90)

  const x1 = cx + radius * Math.cos(startRad)
  const y1 = cy + radius * Math.sin(startRad)
  const x2 = cx + radius * Math.cos(endRad)
  const y2 = cy + radius * Math.sin(endRad)

  const delta = ((endDeg - startDeg) % 360 + 360) % 360
  const largeArc = delta > 180 ? 1 : 0

  return `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`
}

function UtilityFloorPlan({
  activeScene,
  heading = 0,
  fov = 90,
  points = [],
  onPointClick,
  onDetailClick,
  planTang1,
  planTang2,
}) {
  const [collapsed, setCollapsed] = useState(false)
  const [activeFloor, setActiveFloor] = useState(1)
  const [openUtilityMenu, setOpenUtilityMenu] = useState(false)
  const [hoveredPoint, setHoveredPoint] = useState(null)
  const mapRef = useRef(null)
  const [mapSize, setMapSize] = useState({ width: 0, height: 0 })

  // ResizeObserver to update pixel size of the floor plan container
  useEffect(() => {
    if (collapsed || !mapRef.current) return

    const updateSize = () => {
      if (!mapRef.current) return
      setMapSize({
        width: mapRef.current.clientWidth,
        height: mapRef.current.clientHeight,
      })
    }

    updateSize()
    const observer = new ResizeObserver(updateSize)
    observer.observe(mapRef.current)

    return () => observer.disconnect()
  }, [collapsed])

  // Standardize points with percent positions
  const normalizedPoints = useMemo(() => {
    return points.map((p) => ({
      ...p,
      xPercent: toPercentNumber(p.x ?? p.left ?? 0),
      yPercent: toPercentNumber(p.y ?? p.top ?? 0),
    }))
  }, [points])

  // Get active point based on current Krpano scene
  const activePoint = useMemo(() => {
    return normalizedPoints.find((p) => p.scene === activeScene)
  }, [normalizedPoints, activeScene])

  // State for adjusting activeFloor when activePoint changes
  const [prevActivePoint, setPrevActivePoint] = useState(null)
  if (activePoint !== prevActivePoint) {
    setPrevActivePoint(activePoint)
    if (activePoint && activePoint.floor) {
      setActiveFloor(activePoint.floor)
    }
  }

  // Filter points belonging to current active floor that have a valid 360 scene
  const visiblePoints = useMemo(() => {
    return normalizedPoints.filter((p) => p.floor === activeFloor && p.scene)
  }, [normalizedPoints, activeFloor])

  // Calculate radar sector path angles and dynamic radius
  const radarOffset = activePoint?.radarOffset || 0
  const normalizedHeading = ((heading + radarOffset) % 360 + 360) % 360
  // Công thức safeFov: khớp với dải fov thực (fovmin=70 → fovmax=140)
  // Mớ rộng clamp để radar hiển thị đủ dải thu phóng: zoom in → hẹp lại, zoom out → mở rộng
  const safeFov = clamp((fov || 120) * 0.65, 30, 110)
  const startAngle = normalizedHeading - safeFov / 2
  const endAngle = normalizedHeading + safeFov / 2

  const radarRadius = Math.min(mapSize.width, mapSize.height) * 0.2

  const currentPlanImage = activeFloor === 1 ? planTang1 : planTang2

  const [planLoaded, setPlanLoaded] = useState(() => imageLoadCache.has(currentPlanImage))
  useEffect(() => { setPlanLoaded(imageLoadCache.has(currentPlanImage)) }, [currentPlanImage])

  return (
    <div className="utility-floorplan">
      {/* Header section with Action Buttons on the Right */}
      <div className="utility-floorplan__header">
        {/* Spacer on the left to center the title dropdown trigger */}
        <div className="utility-floorplan__header-spacer" />

        {/* Title dropdown trigger (Floor selector) */}
        <button
          type="button"
          onClick={() => setOpenUtilityMenu(!openUtilityMenu)}
          className="utility-floorplan__title-trigger"
          aria-label="Chọn tầng hiển thị"
        >
          <span>{`TẦNG ${activeFloor}`}</span>
          {/* Tam giác đặc: mặc định ▼ xuống, khi open rotate-180 → ▲ lên */}
          <svg
            className={`utility-floorplan__chevron ${openUtilityMenu ? 'rotate-180' : ''}`}
            viewBox="0 0 10 6"
            width="10"
            height="6"
            fill="currentColor"
          >
            <polygon points="0,0 10,0 5,6" />
          </svg>
        </button>

        <div className="utility-floorplan__actions">
          {/* Collapse/Expand button */}
          <button
            type="button"
            onClick={() => {
              setCollapsed(!collapsed)
              setOpenUtilityMenu(false)
            }}
            className="utility-floorplan__action-btn"
            title={collapsed ? 'Mở sơ đồ' : 'Thu gọn'}
          >
            {collapsed ? (
              <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" strokeWidth="2.5" fill="none">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" strokeWidth="2.5" fill="none">
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            )}
          </button>

          {/* Fullscreen/Expand detail button */}
          <button
            type="button"
            onClick={onDetailClick}
            className="utility-floorplan__action-btn"
            title="Phóng to sơ đồ"
          >
            <svg viewBox="0 0 12 12" width="12" height="12" fill="none">
              <path d="M1 4V1H4" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M8 1H11V4" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M11 8V11H8" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M4 11H1V8" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {/* Dropdown list of floors */}
        {openUtilityMenu && (
          <div className={`utility-floorplan__dropdown${collapsed ? ' utility-floorplan__dropdown--above' : ''}`}>
            <button
              type="button"
              onClick={() => {
                if (activeFloor !== 1) {
                  setActiveFloor(1)
                  const targetPoint = normalizedPoints.find((p) => p.floor === 1 && p.scene)
                  if (targetPoint && onPointClick) {
                    onPointClick(targetPoint)
                  }
                }
                setOpenUtilityMenu(false)
              }}
              className={`utility-floorplan__dropdown-item${activeFloor === 1 ? ' utility-floorplan__dropdown-item--active' : ''}`}
            >
              Tầng 1
            </button>
            <button
              type="button"
              onClick={() => {
                if (activeFloor !== 2) {
                  setActiveFloor(2)
                  const targetPoint = normalizedPoints.find((p) => p.floor === 2 && p.scene)
                  if (targetPoint && onPointClick) {
                    onPointClick(targetPoint)
                  }
                }
                setOpenUtilityMenu(false)
              }}
              className={`utility-floorplan__dropdown-item${activeFloor === 2 ? ' utility-floorplan__dropdown-item--active' : ''}`}
            >
              Tầng 2
            </button>
          </div>
        )}
      </div>

      {/* Floorplan Body */}
      {!collapsed && (
        <div className="utility-floorplan__body">
          {/* Map wrapper with styling matching the mock layout border */}
          <div className="utility-floorplan__map-wrapper">
            <div className="utility-floorplan__viewport" style={{ aspectRatio: '1879/1057' }}>
              <div ref={mapRef} className="utility-floorplan__map-container">
                <BlurHashImage hash={BLUR_HASH_MAP.plan} fadeOut={planLoaded} />
                {currentPlanImage && (
                  <img
                    src={currentPlanImage}
                    alt={`Sơ đồ tầng ${activeFloor}`}
                    className="utility-floorplan__bg-img"
                    onLoad={() => { imageLoadCache.add(currentPlanImage); setPlanLoaded(true) }}
                  />
                )}

                {/* Render Radar fan path */}
                {activePoint && activePoint.floor === activeFloor && mapSize.width > 0 && (
                  <svg
                    className="utility-floorplan__radar-svg"
                    viewBox={`0 0 ${mapSize.width} ${mapSize.height}`}
                  >
                    <path
                      d={getSectorPath(
                        (activePoint.xPercent / 100) * mapSize.width,
                        (activePoint.yPercent / 100) * mapSize.height,
                        radarRadius,
                        startAngle,
                        endAngle
                      )}
                      className="utility-floorplan__radar-fan"
                    />
                  </svg>
                )}

                {/* Render Hotspot points */}
                {visiblePoints.map((point) => {
                  const isActive = point.scene === activeScene
                  return (
                    <button
                      key={point.id || point.scene}
                      type="button"
                      onClick={() => onPointClick?.(point)}
                      onMouseEnter={() => setHoveredPoint(point)}
                      onMouseLeave={() => setHoveredPoint(null)}
                      className={`utility-floorplan__hotspot${isActive ? ' utility-floorplan__hotspot--active' : ''}`}
                      style={{ left: `${point.xPercent}%`, top: `${point.yPercent}%` }}
                    />
                  )
                })}
              </div>

              {/* Floating label overlay */}
              {hoveredPoint && (() => {
                const showBelow = hoveredPoint.yPercent < 18
                return (
                  <div
                    className={`utility-floorplan__tooltip ${showBelow ? 'utility-floorplan__tooltip--below' : 'utility-floorplan__tooltip--above'}`}
                    style={{ left: `${hoveredPoint.xPercent}%`, top: `${hoveredPoint.yPercent}%` }}
                  >
                    <span className="utility-floorplan__tooltip-text">
                      {hoveredPoint.label}
                    </span>
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UtilityFloorPlan
